import React, {useEffect, useMemo, useState} from "react";
import {TravelDuration, TravelDurationMap} from "../../models/TravelDuration";
import MonthTimeline from "../input/monthInputTimeline";
import DateRangeCalendar from "../input/dateRangeCalendar";
import {DateSelectorMode, TripType} from "../../models/flightSearchData";
import {getTravelDurationLabel, useTranslation} from "../../i18n/LanguageProvider";

interface TravelDateSelectorProps {
    compact?: boolean;
    dateFrom: Date | null;
    dateTo: Date | null;
    setDates: (from: Date | null, to: Date | null) => void;
    tripType: TripType;
    setTripType: (tripType: TripType) => void;
    travelDuration: TravelDuration;
    setTravelDuration: (duration: TravelDuration) => void;
    minDurationDays: number | null;
    maxDurationDays: number | null;
    setCustomDurationRange: (min: number | null, max: number | null) => void;
    mode: DateSelectorMode;
    setMode: (mode: DateSelectorMode) => void;
}

/** Normalize to first day of month */
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
/** Month difference a->b */
const monthDiff = (a: Date, b: Date) =>
    (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
/** Add i months to base (day=1) */
const addMonths = (base: Date, i: number) =>
    new Date(base.getFullYear(), base.getMonth() + i, 1);

const TravelDateSelector: React.FC<TravelDateSelectorProps> = ({
                                                                   compact = false,
                                                                   dateFrom,
                                                                   dateTo,
                                                                   setDates,
                                                                   tripType,
                                                                   setTripType,
                                                                   travelDuration,
                                                                   setTravelDuration,
                                                                   minDurationDays,
                                                                   maxDurationDays,
                                                                   setCustomDurationRange,
                                                               mode,
                                                               setMode,
                                                           }) => {
    const {locale, t} = useTranslation();
    const base = useMemo(() => startOfMonth(new Date()), []);

    const months = useMemo(() => {
        return Array.from({length: 12}, (_, i) => {
            const date = addMonths(base, i);
            return {
                label: date
                    .toLocaleString(locale, {month: "short"})
                    .toUpperCase(),
                date,
                index: i,
            };
        });
    }, [base, locale]);

    const [range, setRange] = useState<[number, number] | null>(null);

    useEffect(() => {
        if (!months.length) return;

        if (dateFrom && dateTo) {
            const fromIdx = Math.max(
                0,
                Math.min(11, monthDiff(base, startOfMonth(dateFrom)))
            );
            const toIdx = Math.max(
                0,
                Math.min(11, monthDiff(base, startOfMonth(dateTo)))
            );
            const start = Math.min(fromIdx, toIdx);
            const end = Math.max(fromIdx, toIdx);
            setRange([start, end]);
        } else {
            setRange(null);
        }
    }, [months.length, dateFrom, dateTo, base]);

    const handleCustomMinChange = (value: string) => {
        const parsed = value === "" ? null : Math.max(1, Number(value));
        setCustomDurationRange(parsed, maxDurationDays);
    };

    const handleCustomMaxChange = (value: string) => {
        const parsed = value === "" ? null : Math.max(1, Number(value));
        setCustomDurationRange(minDurationDays, parsed);
    };

    const isOneWay = tripType === TripType.OneWay;

    //if (!range) return null;

    const modeControl = (
                <button
                    onClick={() =>
                        setMode(
                            mode === DateSelectorMode.Timeline
                                ? DateSelectorMode.Calendar
                                : DateSelectorMode.Timeline
                        )
                    }
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                     bg-[#7745F5] text-white shadow-md
                     transition-transform duration-200 ease-out
                     hover:scale-105 hover:bg-[#712FFF] active:scale-95"
                >
                    {mode === DateSelectorMode.Timeline ? t("travel.calendar") : t("travel.rangePlanner")}
                </button>
    );

    const customDurationControls = (<>
            {!isOneWay && travelDuration === TravelDuration.CUSTOM && (
                <div className="mb-6 rounded-2xl bg-[#F3F1FF] border border-[#E4DBFF] px-4 py-4">
                    <div className="text-sm font-semibold text-[#4B4B4B] mb-3">
                        {t("travel.customDuration")}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-[#6B6B6B] mb-1">
                                {t("travel.min")}
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={minDurationDays ?? ""}
                                onChange={(e) => handleCustomMinChange(e.target.value)}
                                placeholder={t("travel.sampleMin")}
                                className="w-full rounded-xl border border-[#D8D8D8] bg-white px-4 py-2.5 text-sm text-[#4B4B4B] outline-none focus:border-[#7745F5]"
                            />
                        </div>

                        <div className="flex-1">
                            <label className="block text-xs font-medium text-[#6B6B6B] mb-1">
                                {t("travel.max")}
                            </label>
                            <input
                                type="number"
                                min={1}
                                value={maxDurationDays ?? ""}
                                onChange={(e) => handleCustomMaxChange(e.target.value)}
                                placeholder={t("travel.sampleMax")}
                                className="w-full rounded-xl border border-[#D8D8D8] bg-white px-4 py-2.5 text-sm text-[#4B4B4B] outline-none focus:border-[#7745F5]"
                            />
                        </div>
                    </div>
                </div>
            )}

    </>);

    const tripTypeControls = (
            <div className="mb-6">
                <div className="mb-2 text-xs font-bold text-[#B1A3FF]">{t("travel.tripType")}</div>
                <div className="inline-flex rounded-2xl bg-[#F3F1FF] p-1.5 shadow-inner transition-smooth">
                    <button
                        onClick={() => setTripType(TripType.RoundTrip)}
                        className={`px-3 sm:px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out ${
                            tripType === TripType.RoundTrip
                                ? "bg-white text-accent shadow-sm"
                                : "text-[#8D72E8] hover:text-accent"
                        }`}
                    >
                        {t("travel.roundTrip")}
                    </button>
                    <button
                        onClick={() => setTripType(TripType.OneWay)}
                        className={`px-3 sm:px-5 py-2 rounded-xl text-sm font-bold transition-all duration-300 ease-in-out ${
                            tripType === TripType.OneWay
                                ? "bg-white text-accent shadow-sm"
                                : "text-[#8D72E8] hover:text-accent"
                        }`}
                    >
                        {t("travel.oneWay")}
                    </button>
                </div>
            </div>

    );

    return (
        <div className="selector-panel travel-panel flex flex-col bg-[#FBFBFF] shadow-card rounded-3xl p-4 sm:p-6 w-full">
            {!compact && tripTypeControls}
            {!compact && customDurationControls}

            {/* Duration Buttons */}
            {!isOneWay && <h2 className="wizard-only mobile-section-title">{t("mobile.duration")}</h2>}
            {!isOneWay && (
            <div className="travel-duration-options flex gap-4 mb-6 flex-wrap transition-smooth">
                {(Object.entries(TravelDurationMap) as [
                    TravelDuration,
                    (typeof TravelDurationMap)[TravelDuration]
                ][])
                    .filter(([key]) => !compact || key !== TravelDuration.CUSTOM)
                    .map(([key]) => (
                    <button
                        key={key}
                        aria-pressed={travelDuration === key}
                        data-custom={key === TravelDuration.CUSTOM || undefined}
                        onClick={() => setTravelDuration(key as TravelDuration)}
                        className={`px-5 py-2.5 rounded-xl hover:scale-105 text-sm font-bold transition ${
                            travelDuration === key
                                ? "bg-[#4B4B4B] text-white hover:bg-[#3F3F3F]"
                                : "bg-[#EAEAEA] text-[#4B4B4B] hover:bg-[#D5D5D5]"
                        }`}
                    >
                        <span className="duration-desktop-label">{getTravelDurationLabel(key, t)}</span>
                        <span className="duration-mobile-label">
                            {key === TravelDuration.CUSTOM ? getTravelDurationLabel(key, t) : <>
                                <strong>{TravelDurationMap[key].minDays}–{TravelDurationMap[key].maxDays}</strong>
                                <span>{t("mobile.days")}</span>
                            </>}
                        </span>
                    </button>
                ))}
            </div>
            )}

            <div className="travel-mode-header order-first flex flex-wrap gap-3 justify-between items-start mb-5">
                <h2 className="text-[#7745F5] font-semibold text-sm">
                    <span className="desktop-selector-label">{t("travel.title")}</span><span className="wizard-only">{t("mobile.dateRange")}</span>
                </h2>
                {!compact && modeControl}
            </div>

            {mode === DateSelectorMode.Timeline ? (
                <MonthTimeline
                    months={months}
                    range={range ?? [0, 0]}
                    setRange={(newRange) => {
                        const [s, e] = newRange;
                        setRange(newRange);
                        setDates(addMonths(base, s), addMonths(base, e));
                    }}
                />
            ) : (
                <DateRangeCalendar
                    from={dateFrom}
                    to={dateTo}
                    onChange={(from, to) => setDates(from, to)}
                    minDate={new Date()}
                    maxDate={addMonths(new Date(), 12)}
                    travelDuration={travelDuration}
                    locale={locale}
                />

            )}
            {compact && <details className="mobile-travel-options">
                <summary>{t("mobile.moreTravelOptions")}</summary>
                {tripTypeControls}
                {modeControl}
                {!isOneWay && <button type="button" className="mobile-custom-duration" aria-pressed={travelDuration === TravelDuration.CUSTOM}
                    onClick={() => setTravelDuration(TravelDuration.CUSTOM)}>{t("travel.customDuration")}</button>}
                {customDurationControls}
            </details>}
        </div>
    );
};

export default TravelDateSelector;
